<template>
  <div class="job-detail" v-loading="loading">
    <template v-if="dataInfo.id">
      <!-- 顶栏：鉴别号、订单、状态 -->
      <div class="hero">
        <div class="hero-left">
          <div class="hero-no">{{ dataInfo.inspectionNo || "—" }}</div>
          <div class="hero-sub">
            <span>{{ getDictLabel("orderTypeOptions", dataInfo.orderType) }}</span>
            <span class="sep">|</span>
            <span class="mono">{{ dataInfo.orderNo || "—" }}</span>
            <template v-if="dataInfo.shelfNo">
              <span class="sep">|</span>
              <span>货架 {{ dataInfo.shelfNo }}</span>
            </template>
          </div>
          <div class="hero-time" v-if="timeLineText">{{ timeLineText }}</div>
        </div>
        <div class="hero-right">
          <el-tag :type="statusTagType" effect="dark" size="small">{{ getDictLabel("statusOptions", dataInfo.status) }}</el-tag>
          <el-tag size="small" effect="plain">{{ platformInspectionTypeLabel(dataInfo.inspectionType) }}</el-tag>
          <el-tag v-if="dataInfo.inspectionNoPrintFlag === 1" type="success" size="mini" effect="plain">已打印</el-tag>
        </div>
      </div>

      <!-- 鉴别结论（有任一结论时展示） -->
      <div class="result-row" v-if="hasResultStrip">
        <div class="result-cell" v-if="dataInfo.resultAppearance">
          <span class="result-label">外观</span>
          <span class="result-val" :style="{ color: getDictColor('passFailResultOptions', dataInfo.resultAppearance) }">
            {{ getDictLabel("passFailResultOptions", dataInfo.resultAppearance) }}
          </span>
        </div>
        <div class="result-cell" v-if="showLiquidResultInStrip">
          <span class="result-label">液体</span>
          <span class="result-val">
            <span :style="{ color: getDictColor('passFailResultOptions', dataInfo.resultLiquid) }">
              {{ getDictLabel("passFailResultOptions", dataInfo.resultLiquid) }}
            </span>
            <span v-if="dataInfo.ychl" class="result-sub">（乙醇 {{ dataInfo.ychl }}）</span>
          </span>
        </div>
        <div class="result-cell" v-else-if="showLiquidPendingInStrip">
          <span class="result-label">液体</span>
          <span class="result-val result-val--muted">未鉴别</span>
        </div>
        <div class="result-cell" v-if="dataInfo.result">
          <span class="result-label">鉴别结果</span>
          <span class="result-val" :style="{ color: getDictColor('passFailResultOptions', dataInfo.result) }">
            {{ getDictLabel("passFailResultOptions", dataInfo.result) }}
          </span>
        </div>
        <div class="result-cell" v-if="dataInfo.gradeLevel">
          <span class="result-label">评级</span>
          <span class="result-val">{{ dataInfo.gradeLevel }}<span v-if="dataInfo.gradeTotalScore != null" class="result-sub"> / {{ dataInfo.gradeTotalScore }}分</span></span>
        </div>
      </div>

      <!-- 商品 -->
      <section class="block" v-if="hasProductBlock">
        <div class="block-title">商品</div>
        <div class="product-box">
          <div class="img-list" v-if="productImages.length">
            <el-image
              v-for="(url, idx) in productImages"
              :key="'p-' + idx"
              class="img-item"
              :src="url"
              fit="cover"
              :preview-src-list="productImages"
            />
          </div>
          <div class="product-text">
            <div class="product-title" v-if="dataInfo.productTitle">{{ dataInfo.productTitle }}</div>
            <ul class="kv-list">
              <li v-if="brandText"><span class="k">品牌</span><span class="v">{{ brandText }}</span></li>
              <li v-if="typeText"><span class="k">型号</span><span class="v">{{ typeText }}</span></li>
              <li><span class="k">批次</span><span class="v">{{ dataInfo.batchNum || "—" }}</span></li>
              <li v-if="dataInfo.batchValidDays != null"><span class="k">批次有效</span><span class="v">{{ dataInfo.batchValidDays }} 天</span></li>
              <li><span class="k">分装</span><span class="v">{{ sffzLabel(dataInfo.sffz) }}</span></li>
              <li v-if="specMl"><span class="k">规格</span><span class="v">{{ specMl }}</span></li>
            </ul>
          </div>
        </div>
      </section>

      <!-- 备注 -->
      <section class="block" v-if="dataInfo.orderDepict || dataInfo.adminBz">
        <div class="block-title">备注</div>
        <div class="remark-line" v-if="dataInfo.orderDepict">
          <span class="k">用户</span><span class="v">{{ dataInfo.orderDepict }}</span>
        </div>
        <div class="remark-line" v-if="dataInfo.adminBz">
          <span class="k">管理员</span><span class="v">{{ dataInfo.adminBz }}</span>
        </div>
      </section>

      <!-- 外观 -->
      <section class="block" v-if="showAppearanceSection">
        <div class="block-title">外观鉴别</div>
        <div class="img-list" v-if="realImg.length">
          <div class="img-group-label">实物图</div>
          <div class="img-row">
            <el-image v-for="(item, i) in realImg" :key="'r' + i" class="img-item" :src="item" fit="cover" :preview-src-list="realImg" />
          </div>
        </div>
        <div class="img-list" v-if="cxImg.length">
          <div class="img-group-label">瑕疵图</div>
          <div class="img-row">
            <el-image v-for="(item, i) in cxImg" :key="'c' + i" class="img-item" :src="item" fit="cover" :preview-src-list="cxImg" />
          </div>
        </div>
        <template v-if="showRealPictureUpload">
          <p class="empty-tip">暂无实物图，可在此补传（不分装样品建议上传正/背/顶/底等）</p>
          <div class="appearance-upload">
            <orderRealPictureUpload v-model="realPictureDraft" :max="8" :columns="4" />
            <el-button
              type="primary"
              size="small"
              class="appearance-upload__save"
              :loading="realPictureSaving"
              :disabled="!realPictureDraft.length"
              @click="saveRealPictures"
            >保存实物图</el-button>
          </div>
        </template>
        <p v-else-if="!hasAppearancePictures && !dataInfo.resultAppearance" class="empty-tip">暂无外观图片与结论</p>
      </section>

      <!-- 液体 -->
      <section class="block" v-if="showLiquidSection">
        <div class="block-title">液体鉴别</div>
        <el-image
          v-if="dataInfo.jbtpUrl"
          class="spectrum"
          :src="dataInfo.jbtpUrl"
          fit="contain"
          :preview-src-list="[dataInfo.jbtpUrl]"
        />
        <template v-else>
          <p class="empty-tip">
            {{ showLiquidTxtUpload ? "暂无鉴别图谱，可上传仪器 TXT 自动生成" : "暂无鉴别图谱" }}
          </p>
          <div v-if="showLiquidTxtUpload" class="liquid-txt-upload">
            <el-upload
              :show-file-list="false"
              accept=".txt,text/plain"
              action=""
              :http-request="uploadLiquidSpectrumTxt"
              :disabled="liquidTxtUploading"
            >
              <el-button type="primary" size="small" :loading="liquidTxtUploading">
                上传 TXT 生成图谱
              </el-button>
            </el-upload>
          </div>
        </template>
      </section>

      <!-- 报告 -->
      <section class="block" v-if="dataInfo.inspectionReportUrl || (showResultSection && dataInfo.remainDesc)">
        <div class="block-title">报告与余量</div>
        <p v-if="dataInfo.remainDesc" class="remain-desc"><span class="k">余量</span>{{ dataInfo.remainDesc }}</p>
        <el-image
          v-if="dataInfo.inspectionReportUrl"
          class="report"
          :src="dataInfo.inspectionReportUrl"
          fit="contain"
          :preview-src-list="[dataInfo.inspectionReportUrl]"
        />
      </section>

      <!-- 物流 -->
      <section class="block block--inline">
        <div class="block-title">物流</div>
        <div class="inline-pair">
          <span class="k">寄出物流</span>
          <span class="v">{{ outLogisticsLine }}</span>
        </div>
        <div class="inline-pair">
          <span class="k">回寄地址</span>
          <span class="v">{{ receiverLine }}</span>
        </div>
        <div class="inline-pair inline-pair--action">
          <span class="k">回寄物流</span>
          <span class="v">
            {{ returnLogisticsLine }}
            <el-button
              v-if="canEditReturnLogistics"
              type="text"
              size="mini"
              class="inline-action-btn"
              @click="openReturnLogisticsDialog"
            >修改单号</el-button>
          </span>
        </div>
      </section>
    </template>

    <el-dialog
      title="修改回寄物流单号"
      :visible.sync="returnLogisticsVisible"
      width="480px"
      append-to-body
      :close-on-click-modal="false"
      @closed="onReturnLogisticsDialogClosed"
    >
      <el-form label-width="90px">
        <el-form-item label="物流单号" required>
          <el-input
            v-model="returnLogisticsForm.expressNo"
            placeholder="请输入物流单号"
            maxlength="60"
            @input="onReturnLogisticsNoInput"
          />
        </el-form-item>
        <el-form-item label="物流公司" required>
          <el-select
            v-model="returnLogisticsForm.expressCompany"
            placeholder="请选择或自动识别"
            filterable
            style="width: 100%"
          >
            <el-option v-for="item in companyData" :key="item.code" :label="item.title" :value="item.code" />
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="returnLogisticsVisible = false">取消</el-button>
        <el-button type="primary" :loading="returnLogisticsSaving" @click="submitReturnLogistics">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { api } from "@/util/requestUrl";
import expressInfo from "@/util/expressInfo";
import {
  brandDisplay,
  formatInspectionDateTime,
  formatReceiverLine,
  parsePictureUrls,
  platformInspectionTypeLabel,
  productImageUrls,
  sffzLabel,
  specificationMl,
  typeDisplay,
} from "@/util/inspectionJobDisplay";
import orderRealPictureUpload from "@/components/orderRealPictureUpload";

export default {
  name: "InspectionJobDetail",
  components: {
    orderRealPictureUpload,
  },
  props: {
    jobId: { type: [String, Number], required: true },
    previewRow: { type: Object, default: null },
  },
  data() {
    return {
      loading: false,
      dataInfo: {},
      realImg: [],
      cxImg: [],
      companyData: expressInfo.arr || [],
      companyObj: expressInfo.obj || {},
      returnLogisticsVisible: false,
      returnLogisticsSaving: false,
      returnLogisticsRecognizeTimer: null,
      returnLogisticsForm: {
        expressCompany: "",
        expressNo: "",
      },
      liquidTxtUploading: false,
      realPictureDraft: [],
      realPictureSaving: false,
      dicts: {
        statusOptions: [
          { value: 1, label: "平台鉴别中", color: "#E6A23C", tag: "warning" },
          { value: 2, label: "待鉴别结果出具", color: "#409EFF", tag: "primary" },
          { value: 3, label: "待平台寄售评级", color: "#409EFF", tag: "info" },
          { value: 4, label: "待发货", color: "#67C23A", tag: "success" },
          { value: 5, label: "已完成", color: "#909399", tag: "info" },
        ],
        orderTypeOptions: [
          { value: 1, label: "寄售订单" },
          { value: 2, label: "鉴别订单" },
          { value: 3, label: "闲置订单" },
          { value: 4, label: "安心购订单" },
        ],
        passFailResultOptions: [
          { value: 1, label: "通过", color: "#67C23A" },
          { value: 2, label: "不通过", color: "#F56C6C" },
        ],
      },
    };
  },
  computed: {
    statusTagType() {
      const map = { 1: "warning", 2: "", 3: "info", 4: "success", 5: "info" };
      return map[Number(this.dataInfo.status)] || "info";
    },
    productImages() {
      return productImageUrls(this.dataInfo);
    },
    brandText() {
      return brandDisplay(this.dataInfo);
    },
    typeText() {
      return typeDisplay(this.dataInfo);
    },
    specMl() {
      return specificationMl(this.dataInfo.specification);
    },
    timeLineText() {
      const end = this.dataInfo.inspectionEndTime;
      const start = this.dataInfo.inspectionStartTime || this.dataInfo.createTime;
      if (end) return "完成于 " + formatInspectionDateTime(end);
      if (start) return "开始于 " + formatInspectionDateTime(start);
      return "";
    },
    receiverLine() {
      return formatReceiverLine(this.dataInfo);
    },
    outLogisticsLine() {
      const company = this.expressCompanyTitle(this.dataInfo.logisticsCompany);
      const no = (this.dataInfo.logisticsNo || "").trim();
      if (!company && !no) return "—";
      return [company, no].filter(Boolean).join(" ");
    },
    returnLogisticsLine() {
      const company = this.expressCompanyTitle(this.dataInfo.returnLogisticsCompany);
      const no = (this.dataInfo.returnLogisticsNo || "").trim();
      if (!company && !no) return "—";
      return [company, no].filter(Boolean).join(" ");
    },
    canEditReturnLogistics() {
      const kind = this.dataInfo.returnLogisticsUpdateKind;
      if (kind === "order_hxinfo") {
        return !!(this.dataInfo.orderId && [1, 2].includes(Number(this.dataInfo.orderType)));
      }
      if (kind === "center_to_seller" || kind === "center_to_buyer") {
        return !!(this.dataInfo.orderId && [3, 4].includes(Number(this.dataInfo.orderType)));
      }
      return !!(this.dataInfo.orderId && [1, 2].includes(Number(this.dataInfo.orderType)));
    },
    hasResultStrip() {
      return !!(
        this.dataInfo.resultAppearance ||
        this.dataInfo.resultLiquid ||
        this.dataInfo.result ||
        this.dataInfo.gradeLevel
      );
    },
    showLiquidSection() {
      const t = Number(this.dataInfo.inspectionType);
      return t === 1 || t === 2 || this.dataInfo.jbtpUrl || this.dataInfo.resultLiquid || this.dataInfo.ychl;
    },
    /** 外观+液体/寄售+液体，且尚无样品谱图：详情页可上传 TXT */
    showLiquidTxtUpload() {
      const t = Number(this.dataInfo.inspectionType);
      if (t !== 1 && t !== 2) return false;
      return !this.dataInfo.jbtpUrl;
    },
    /** 已实际上传图谱/乙醇，可展示液体结论 */
    liquidInspectionDone() {
      return !!(this.dataInfo.jbtpUrl || this.dataInfo.ychl);
    },
    showLiquidResultInStrip() {
      if (!this.showLiquidSection) return false;
      const rl = Number(this.dataInfo.resultLiquid);
      if (rl !== 1 && rl !== 2) return false;
      return this.liquidInspectionDone;
    },
    /** 外观+液体档，外观不通过导致液体未做 */
    showLiquidPendingInStrip() {
      if (!this.showLiquidSection) return false;
      const t = Number(this.dataInfo.inspectionType);
      if (t !== 1 && t !== 2) return false;
      if (Number(this.dataInfo.resultAppearance) !== 2) return false;
      return !this.liquidInspectionDone;
    },
    showAppearanceSection() {
      const t = Number(this.dataInfo.inspectionType);
      return t === 3 || this.hasAppearancePictures || this.dataInfo.resultAppearance || this.showRealPictureUpload;
    },
    /** 不分装且尚无实物图：详情页可补传 */
    showRealPictureUpload() {
      if (!this.dataInfo || !this.dataInfo.id) return false;
      const sffz = this.dataInfo.sffz;
      if (sffz != null && sffz !== "" && Number(sffz) === 0) return false;
      return this.realImg.length === 0;
    },
    showResultSection() {
      return Number(this.dataInfo.status) > 1;
    },
    hasProductBlock() {
      return !!(
        this.dataInfo.productTitle ||
        this.productImages.length ||
        this.brandText ||
        this.typeText
      );
    },
    hasAppearancePictures() {
      return this.realImg.length > 0 || this.cxImg.length > 0;
    },
  },
  watch: {
    jobId: {
      immediate: true,
      handler(id) {
        if (id) this.loadData();
      },
    },
  },
  beforeDestroy() {
    if (this.returnLogisticsRecognizeTimer) {
      clearTimeout(this.returnLogisticsRecognizeTimer);
      this.returnLogisticsRecognizeTimer = null;
    }
  },
  methods: {
    platformInspectionTypeLabel,
    sffzLabel,
    expressCompanyTitle(code) {
      const raw = (code || "").trim();
      if (!raw) return "";
      const hit = this.companyObj[raw];
      if (hit && hit.title) return hit.title;
      const lower = raw.toLowerCase();
      const byCode = (this.companyData || []).find((item) => item.code === lower || item.code === raw);
      if (byCode) return byCode.title;
      const byTitle = (this.companyData || []).find((item) => item.title === raw);
      return byTitle ? byTitle.title : raw;
    },
    mapExpressCompanyToCode(company) {
      const raw = (company || "").trim();
      if (!raw) return "";
      const lower = raw.toLowerCase();
      const hit = this.companyObj[lower] || this.companyObj[raw];
      if (hit && hit.code) return hit.code;
      const byCode = (this.companyData || []).find((item) => item.code === lower || item.code === raw);
      if (byCode) return byCode.code;
      const byTitle = (this.companyData || []).find((item) => item.title === raw);
      if (byTitle) return byTitle.code;
      for (const item of this.companyData || []) {
        if (item.title === raw || item.code === lower) return item.code;
        if (raw.includes(item.title) || item.title.includes(raw)) return item.code;
      }
      return lower.replace(/\s/g, "");
    },
    openReturnLogisticsDialog() {
      const companyCode = this.mapExpressCompanyToCode(this.dataInfo.returnLogisticsCompany);
      this.returnLogisticsForm = {
        expressCompany: companyCode,
        expressNo: (this.dataInfo.returnLogisticsNo || "").trim(),
      };
      this.returnLogisticsVisible = true;
    },
    onReturnLogisticsDialogClosed() {
      if (this.returnLogisticsRecognizeTimer) {
        clearTimeout(this.returnLogisticsRecognizeTimer);
        this.returnLogisticsRecognizeTimer = null;
      }
      this.returnLogisticsForm = { expressCompany: "", expressNo: "" };
    },
    onReturnLogisticsNoInput(val) {
      if (this.returnLogisticsRecognizeTimer) {
        clearTimeout(this.returnLogisticsRecognizeTimer);
        this.returnLogisticsRecognizeTimer = null;
      }
      const trimmed = String(val || "").trim();
      if (!trimmed) {
        this.returnLogisticsForm.expressCompany = "";
        return;
      }
      if (trimmed.length < 6) return;
      this.returnLogisticsRecognizeTimer = setTimeout(() => {
        this.returnLogisticsRecognizeTimer = null;
        this.fetchReturnLogisticsCompany(trimmed);
      }, 500);
    },
    fetchReturnLogisticsCompany(num) {
      if (!num || !api.kuaidiCloudAutoNumber) return;
      const url = `${api.kuaidiCloudAutoNumber}?num=${encodeURIComponent(num)}`;
      this.$axios
        .get(url)
        .then((res) => {
          if (!res.data || res.data.code !== 200) return;
          let payload = res.data.data;
          if (payload == null || payload === "") return;
          if (typeof payload === "string") {
            try {
              payload = JSON.parse(payload);
            } catch (e) {
              return;
            }
          }
          if (payload && payload.returnCode != null) return;
          const arr = Array.isArray(payload) ? payload : null;
          if (!arr || !arr.length) return;
          const first = arr[0] || {};
          const comCode = String(first.comCode || "").trim();
          if (!comCode) return;
          const currentNo = String(this.returnLogisticsForm.expressNo || "").trim();
          if (currentNo !== String(num).trim()) return;
          this.returnLogisticsForm.expressCompany = comCode;
        })
        .catch(() => {});
    },
    submitReturnLogistics() {
      const company = (this.returnLogisticsForm.expressCompany || "").trim();
      const no = (this.returnLogisticsForm.expressNo || "").trim();
      if (!no) {
        this.$message.error("请填写物流单号");
        return;
      }
      if (!company) {
        this.$message.error("请选择物流公司");
        return;
      }
      const kind = this.dataInfo.returnLogisticsUpdateKind
        || ([1, 2].includes(Number(this.dataInfo.orderType)) ? "order_hxinfo" : "");
      let requestUrl;
      let requestData;
      const companyItem = (this.companyData || []).find((item) => item.code === company);
      const companyTitle = companyItem ? companyItem.title : company;

      if (kind === "order_hxinfo") {
        if (!this.dataInfo.orderId) {
          this.$message.error("缺少订单ID");
          return;
        }
        requestUrl = api.upOrder;
        requestData = {
          id: this.dataInfo.orderId,
          hxinfo: JSON.stringify({
            code: company,
            phone: this.dataInfo.receiverPhone || "",
            title: companyTitle,
            num: no,
          }),
        };
      } else if (kind === "center_to_seller") {
        if (!this.dataInfo.orderId) {
          this.$message.error("缺少订单ID");
          return;
        }
        requestUrl = api.updateSecondhandOrder;
        requestData = {
          id: this.dataInfo.orderId,
          centerToSellerExpressCompany: company,
          centerToSellerExpressNo: no,
        };
      } else if (kind === "center_to_buyer") {
        if (!this.dataInfo.orderId) {
          this.$message.error("缺少订单ID");
          return;
        }
        requestUrl = api.updateSecondhandOrder;
        requestData = {
          id: this.dataInfo.orderId,
          centerToBuyerExpressCompany: company,
          centerToBuyerExpressNo: no,
        };
      } else {
        this.$message.error("当前订单类型不支持修改回寄物流");
        return;
      }

      this.returnLogisticsSaving = true;
      this.$axios
        .post(requestUrl, requestData)
        .then((res) => {
          if (res.data && res.data.code === 200) {
            this.$message.success("回寄物流已更新");
            this.returnLogisticsVisible = false;
            this.loadData();
          } else {
            this.$message.error((res.data && res.data.msg) || "更新失败");
          }
        })
        .catch(() => this.$message.error("网络异常"))
        .finally(() => {
          this.returnLogisticsSaving = false;
        });
    },
    applyRow(row) {
      if (!row) return;
      this.dataInfo = { ...row };
      this.realImg = parsePictureUrls(row.realpicture);
      this.cxImg = parsePictureUrls(row.defectImgs);
      this.realPictureDraft = [];
    },
    loadData() {
      if (this.previewRow) this.applyRow(this.previewRow);
      this.loading = true;
      this.$axios
        .get(api.getInspectionJobById, { params: { id: this.jobId } })
        .then((res) => {
          if (res.data.code === 200 && res.data.data) {
            const d = res.data.data;
            this.dataInfo = d;
            this.realImg = parsePictureUrls(d.realpicture);
            this.cxImg = parsePictureUrls(d.defectImgs);
            this.$emit("loaded", d);
          } else {
            this.$message.error(res.data.msg || "获取详情失败");
          }
        })
        .catch(() => this.$message.error("网络异常"))
        .finally(() => {
          this.loading = false;
        });
    },
    uploadLiquidSpectrumTxt({ file }) {
      if (!file || !this.jobId) return;
      const name = (file.name || "").toLowerCase();
      if (!name.endsWith(".txt")) {
        this.$message.warning("请选择 .txt 文件");
        return;
      }
      const formData = new FormData();
      formData.append("jobId", this.jobId);
      formData.append("file", file);
      this.liquidTxtUploading = true;
      this.$axios
        .post(api.uploadSpectrumTxtForJob, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((res) => {
          if (res.data && res.data.code === 200) {
            this.$message.success(res.data.msg || "谱图已生成");
            if (res.data.data) {
              this.applyRow(res.data.data);
            } else {
              this.loadData();
            }
          } else {
            this.$message.error((res.data && res.data.msg) || "上传失败");
          }
        })
        .catch(() => this.$message.error("网络异常"))
        .finally(() => {
          this.liquidTxtUploading = false;
        });
    },
    saveRealPictures() {
      if (!this.jobId) return;
      if (!this.realPictureDraft.length) {
        this.$message.warning("请先上传实物图");
        return;
      }
      this.realPictureSaving = true;
      this.$axios
        .post(api.updateInspectionJobSamplePictures, {
          id: this.jobId,
          realpicture: JSON.stringify(this.realPictureDraft),
        })
        .then((res) => {
          if (res.data && res.data.code === 200) {
            this.$message.success(res.data.msg || "实物图已保存");
            if (res.data.data) {
              this.applyRow(res.data.data);
            } else {
              this.loadData();
            }
          } else {
            this.$message.error((res.data && res.data.msg) || "保存失败");
          }
        })
        .catch(() => this.$message.error("网络异常"))
        .finally(() => {
          this.realPictureSaving = false;
        });
    },
    getDictLabel(dictKey, value) {
      if (value == null) return "—";
      const item = (this.dicts[dictKey] || []).find((d) => d.value === value);
      return item ? item.label : "—";
    },
    getDictColor(dictKey, value) {
      const item = (this.dicts[dictKey] || []).find((d) => d.value === value);
      return item ? item.color : "#303133";
    },
  },
};
</script>

<style scoped>
.job-detail {
  padding: 0 8px 16px;
  min-height: 100px;
  color: #303133;
  font-size: 14px;
}

/* 顶栏 */
.hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}

.hero-no {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: #303133;
  line-height: 1.4;
}

.hero-sub {
  margin-top: 6px;
  font-size: 13px;
  color: #606266;
}

.hero-sub .mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
}

.hero-sub .sep {
  margin: 0 8px;
  color: #dcdfe6;
}

.hero-time {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}

.hero-right {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
  flex-shrink: 0;
}

/* 结论条 */
.result-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
  padding: 12px 16px;
  margin-bottom: 20px;
  background: #fafafa;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
}

.result-cell {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.result-label {
  font-size: 12px;
  color: #909399;
  flex-shrink: 0;
}

.result-val {
  font-size: 14px;
  font-weight: 600;
}

.result-val--muted {
  font-weight: normal;
  color: #c0c4cc;
}

.result-sub {
  font-size: 12px;
  font-weight: normal;
  color: #909399;
}

/* 分区 */
.block {
  margin-bottom: 22px;
}

.block-title {
  font-size: 13px;
  font-weight: 600;
  color: #909399;
  margin-bottom: 10px;
  padding-left: 8px;
  border-left: 3px solid #409eff;
}

.block--inline .inline-pair {
  margin-bottom: 8px;
  font-size: 13px;
  line-height: 1.6;
}

.block--inline .inline-pair--action .v {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.block--inline .k {
  display: inline-block;
  width: 64px;
  color: #909399;
  vertical-align: top;
}

.block--inline .v {
  color: #606266;
}

.inline-action-btn {
  padding: 0 4px;
  margin-left: 4px;
}

.product-box {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.product-title {
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 10px;
  line-height: 1.5;
}

.kv-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.kv-list li {
  display: flex;
  margin-bottom: 6px;
  font-size: 13px;
  line-height: 1.5;
}

.kv-list .k {
  width: 56px;
  flex-shrink: 0;
  color: #909399;
}

.kv-list .v {
  flex: 1;
  color: #606266;
}

.remark-line {
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 6px;
}

.remark-line .k {
  color: #909399;
  margin-right: 8px;
}

.remark-line .v {
  color: #606266;
}

.img-list {
  margin-bottom: 10px;
}

.img-group-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.img-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.img-item {
  width: 88px;
  height: 88px;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f7fa;
}

.spectrum,
.report {
  max-width: 100%;
  max-height: 320px;
  border-radius: 4px;
  background: #f5f7fa;
}

.remain-desc {
  margin: 0 0 10px;
  font-size: 13px;
  color: #606266;
}

.remain-desc .k {
  color: #909399;
  margin-right: 8px;
}

.empty-tip {
  margin: 0;
  font-size: 13px;
  color: #c0c4cc;
}

.liquid-txt-upload {
  margin-top: 12px;
}

.appearance-upload {
  margin-top: 12px;
}

.appearance-upload__save {
  margin-top: 12px;
}
</style>



// WEBPACK FOOTER //
// src/pages/inspectionJob/detail.vue